/**
 * URL <-> teleconsultation worklist view-state.
 *
 * The TC worklist keeps its whole view state — tab, search boxes, multi-select
 * filters, mode + saved worklist, sort, and pagination — in the URL query
 * string so that leaving for the viewer or report and coming back restores the
 * exact view, and a copied URL reproduces it for someone else. A per-tab
 * sessionStorage memory of the last serialized state (below) covers returns
 * through a *bare* `/teleconsultation` — the sidebar, TopNav, or any other
 * page's back path — where the URL carries nothing to hydrate from.
 *
 * Defaults are deliberately *omitted* from the URL (page=1, the default sort,
 * empty searches and lists, …) so a shared URL only carries what the user
 * changed. The `tab` param is the one exception: it is ALWAYS emitted, because
 * a bare `/teleconsultation` is the signal to restore per-tab memory. Omitting
 * a default tab would make "show me all cases" indistinguishable from "take me
 * back to whatever I was looking at", so an intentional default view has to
 * say `tab=all` out loud.
 * Param names predating this module (tab, clinicId, groupId, regionId, unread,
 * statUrgent, favorites, link, page) are kept for back-compat with old links.
 */
/**
 * Default ordering is priority ascending — STAT first, then urgent, then
 * routine — with the backend breaking ties by oldest submission first, so the
 * case that has been waiting longest at the highest priority is at the top of
 * every fresh worklist (and is what "Next STAT" hands the reader).
 */
export const TC_WORKLIST_DEFAULTS = {
    tab: 'all',
    assignedTo: 'all',
    worklistMode: 'static',
    activeSavedWorklistId: null,
    sortBy: 'priority',
    sortDir: 'asc',
    page: 1,
    pageSize: 25,
};
// An unknown tab from a hand-edited URL must fall back to the default,
// otherwise the tab strip renders with no active tab.
const TC_WORKLIST_TABS = [
    'all',
    'my_queue',
    'group_queue',
    'pending_review',
    'completed',
];
// Same guard for the worklist mode (`mode=` param).
const TC_WORKLIST_MODES = [
    'static',
    'algorithmic',
    'custom',
];
// Multi-select filters round-trip as comma-joined lists under these names.
// Values are validated loosely (like the study worklist's sortBy): the toolbar
// only emits known values and the backend ignores unknown ones.
const LIST_PARAM_KEYS = ['status', 'priority', 'modality', 'species', 'bodyArea'];
// Report-section scope is a tri-state where 'all' is the default and is never
// serialized (mirrors how lib/api/teleconsultation.ts sends it).
const REPORT_SECTIONS = ['impression', 'findings'];
/**
 * Every param this module owns. Used to tell a URL that addresses the TC
 * worklist from one that carries nothing about it (see overlayTcWorklistSearch).
 */
const TC_WORKLIST_PARAM_KEYS = new Set([
    'tab',
    'mode',
    'savedWorklist',
    'clinicId',
    'groupId',
    'regionId',
    'search',
    'reportKeyword',
    'reportSection',
    'tag',
    ...LIST_PARAM_KEYS,
    'assignedTo',
    'unread',
    'statUrgent',
    'favorites',
    'link',
    'sortBy',
    'sortDir',
    'page',
    'pageSize',
]);
export const emptyTcWorklistFilters = {
    clinicId: null,
    groupId: null,
    status: [],
    priority: [],
    modality: [],
    species: [],
    bodyArea: [],
    assignedTo: 'all',
    search: '',
    regionId: null,
    unreadOnly: false,
    priorityStatUrgent: false,
    favoritesOnly: false,
    linkOnly: false,
};
function parseList(value) {
    return value ? value.split(',').filter(Boolean) : [];
}
/** Parse a URL search string into TC worklist view state (defaults applied). */
export function parseTcWorklistState(search) {
    const params = new URLSearchParams(search);
    const tab = params.get('tab');
    const assignedTo = params.get('assignedTo');
    const mode = params.get('mode');
    const reportSection = params.get('reportSection');
    const sortDir = params.get('sortDir');
    const page = Number(params.get('page'));
    const pageSize = Number(params.get('pageSize'));
    const worklistMode = TC_WORKLIST_MODES.includes(mode)
        ? mode
        : TC_WORKLIST_DEFAULTS.worklistMode;
    return {
        activeTab: TC_WORKLIST_TABS.includes(tab)
            ? tab
            : TC_WORKLIST_DEFAULTS.tab,
        worklistMode,
        // A saved-worklist id only means something in custom mode (the reducer
        // drops it whenever the mode leaves 'custom'); parsing mirrors that so a
        // hand-edited URL can never produce an inconsistent pair.
        activeSavedWorklistId: worklistMode === 'custom' ? params.get('savedWorklist') || null : null,
        filters: {
            ...emptyTcWorklistFilters,
            clinicId: params.get('clinicId') || null,
            groupId: params.get('groupId') || null,
            regionId: params.get('regionId') || null,
            search: params.get('search') || '',
            reportKeyword: params.get('reportKeyword') || undefined,
            // Both are real server-side filters (lib/api/teleconsultation.ts sends
            // them and they are part of the worklist query key), so they have to
            // round-trip or the clinical-tag filter is lost on every navigation.
            reportSection: REPORT_SECTIONS.includes(reportSection)
                ? reportSection
                : undefined,
            tag: params.get('tag') || undefined,
            status: parseList(params.get('status')),
            priority: parseList(params.get('priority')),
            modality: parseList(params.get('modality')),
            species: parseList(params.get('species')),
            bodyArea: parseList(params.get('bodyArea')),
            assignedTo: assignedTo === 'me' || assignedTo === 'unassigned'
                ? assignedTo
                : TC_WORKLIST_DEFAULTS.assignedTo,
            unreadOnly: params.get('unread') === 'true',
            priorityStatUrgent: params.get('statUrgent') === 'true',
            favoritesOnly: params.get('favorites') === 'true',
            linkOnly: params.get('link') === 'true',
        },
        sortBy: params.get('sortBy') || TC_WORKLIST_DEFAULTS.sortBy,
        // Both directions have to be recognised explicitly: the default is 'asc',
        // so treating "not 'asc'" as 'desc' (or vice versa) would swallow the
        // other direction from every old link that spells it out.
        sortDir: sortDir === 'asc' || sortDir === 'desc' ? sortDir : TC_WORKLIST_DEFAULTS.sortDir,
        page: Number.isInteger(page) && page >= 1 ? page : TC_WORKLIST_DEFAULTS.page,
        pageSize: Number.isInteger(pageSize) && pageSize >= 1 ? pageSize : TC_WORKLIST_DEFAULTS.pageSize,
    };
}
/** Serialize TC worklist view state to a URL search string, omitting defaults (except `tab`). */
export function serializeTcWorklistState(state) {
    const params = new URLSearchParams();
    const { activeTab, filters, worklistMode, activeSavedWorklistId, sortBy, sortDir, page, pageSize, } = state;
    // Always emitted, default included — see the module comment: a bare URL means
    // "restore my last view", so a deliberate all-cases view must be explicit.
    params.set('tab', activeTab);
    if (worklistMode !== TC_WORKLIST_DEFAULTS.worklistMode)
        params.set('mode', worklistMode);
    // Only meaningful in custom mode — never leak a stale id into other modes.
    if (worklistMode === 'custom' && activeSavedWorklistId) {
        params.set('savedWorklist', activeSavedWorklistId);
    }
    if (filters.clinicId)
        params.set('clinicId', filters.clinicId);
    if (filters.groupId)
        params.set('groupId', filters.groupId);
    if (filters.regionId)
        params.set('regionId', filters.regionId);
    if (filters.search)
        params.set('search', filters.search);
    if (filters.reportKeyword)
        params.set('reportKeyword', filters.reportKeyword);
    // 'all' is the default scope and stays out of the URL, like the API call.
    if (filters.reportSection && filters.reportSection !== 'all') {
        params.set('reportSection', filters.reportSection);
    }
    if (filters.tag)
        params.set('tag', filters.tag);
    for (const key of LIST_PARAM_KEYS) {
        if (filters[key].length > 0)
            params.set(key, filters[key].join(','));
    }
    if (filters.assignedTo !== TC_WORKLIST_DEFAULTS.assignedTo) {
        params.set('assignedTo', filters.assignedTo);
    }
    if (filters.unreadOnly)
        params.set('unread', 'true');
    if (filters.priorityStatUrgent)
        params.set('statUrgent', 'true');
    if (filters.favoritesOnly)
        params.set('favorites', 'true');
    if (filters.linkOnly)
        params.set('link', 'true');
    if (sortBy !== TC_WORKLIST_DEFAULTS.sortBy)
        params.set('sortBy', sortBy);
    if (sortDir !== TC_WORKLIST_DEFAULTS.sortDir)
        params.set('sortDir', sortDir);
    if (page !== TC_WORKLIST_DEFAULTS.page)
        params.set('page', String(page));
    if (pageSize !== TC_WORKLIST_DEFAULTS.pageSize)
        params.set('pageSize', String(pageSize));
    // Deterministic param order keeps shared URLs stable and tests predictable.
    params.sort();
    return params.toString();
}
/** True when the URL addresses the TC worklist at all (carries any owned param). */
export function hasTcWorklistParams(search) {
    for (const key of new URLSearchParams(search).keys()) {
        if (TC_WORKLIST_PARAM_KEYS.has(key))
            return true;
    }
    return false;
}
/**
 * Decide what a mounting provider hydrates from: the URL or the remembered
 * per-tab view.
 *
 * A URL that carries ANY worklist param is authoritative and is used whole —
 * defaults included. Because serialization omits defaults, a URL cannot say
 * "this filter is OFF"; merging remembered state underneath it would therefore
 * let a link shared between two radiologists resolve to two different views
 * (each recipient's leftover search/filters bleeding in). Treating the link as
 * the complete view makes it deterministic and reproducible for anyone.
 *
 * Only a BARE URL — no worklist params at all, i.e. returning through the
 * sidebar, TopNav, or another page's back path — restores the remembered view.
 */
export function overlayTcWorklistSearch(remembered, current) {
    if (!remembered)
        return current;
    return hasTcWorklistParams(current) ? current : remembered;
}
/**
 * Per-tab memory of the last serialized TC worklist view (same pattern as
 * lib/clinicFilterMemory). Case round-trips restore via the tc.returnState
 * marker's URL, but navigating to ANY other page (Analytics, Studies, billing,
 * settings) and back lands on a bare /teleconsultation whose provider remount
 * would otherwise hydrate pure defaults. The provider re-remembers on every
 * URL write-back and recalls on mount; cleared on logout/login by
 * clearPerTabSessionMemory so a shared workstation never inherits a view.
 */
const TC_WORKLIST_STATE_KEY = 'tc.worklistState';
export function rememberTcWorklistState(search) {
    try {
        sessionStorage.setItem(TC_WORKLIST_STATE_KEY, search);
    }
    catch {
        // Storage unavailable — the view simply won't stick; never throw.
    }
}
export function recallTcWorklistState() {
    try {
        return sessionStorage.getItem(TC_WORKLIST_STATE_KEY);
    }
    catch {
        return null;
    }
}
export function clearTcWorklistState() {
    try {
        sessionStorage.removeItem(TC_WORKLIST_STATE_KEY);
    }
    catch {
        // Storage unavailable — nothing was stored anyway.
    }
}
